import { Component, Input, DoCheck, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';

@Component({
    selector: 'app-rewards-track-input',
    templateUrl: './rewards-track-input.component.html',
    styleUrls: ['./rewards-track-input.component.css']
})
export class RewardsTrackInputComponent implements DoCheck, OnInit {
    constructor() { }

    @Input() field: any;
    @Input() record: any;
    @Output() onRecordChange = new EventEmitter<any>();
    private previousType: number | undefined;

    trackKeys: string[] = [];
    trackKeyLabels: string[] = [];
    multiKeyStages: boolean = false;

    milestoneLabel: string = 'Target';
    touchIsLoop: boolean = false;
    showIsLoop: boolean = false;
    /**
     * Types whose stageMilestone is a fixed, backend-enforced sequence (see
     * RewardsTrackPresave in the API). There's no input control for it and this
     * component must not try to reconcile its length client-side.
     */
    readonly fixedStageMilestoneTypes: number[] = [1, 2];
    readonly trackKeyType: { [key: string]: string } = {
        dailyRewardDay1: 'singleStage',
        dailyRewardDay2: 'singleStage',
        dailyRewardDay3: 'singleStage',
        dailyRewardDay4: 'singleStage',
        dailyRewardDay5: 'singleStage',
        dailyRewardDay6: 'singleStage',
        dailyRewardDay7: 'singleStage',
        dailyRewardDay30: 'singleStage',
        free: 'multiStage',
        paid: 'multiStage',
        friends: 'multiStage',
        EOrewards: 'eoTrack',
        EObundles: 'eoTrack',
        coop: 'multiStage',
        miniGamesRewards: 'multiStage',
        seasonalPrizes: 'multiStage',
        dailyActivitiesRewards: 'multiStage',
        perkDailyReward: 'multiStage',
        giftingTreeV2Rewards: 'multiStage',
    };

    /**
     * Gets the numeric type value from the record
     * Handles cases where type might be a template _id instead of a number
     */
    private getNumericType(): number | undefined {
        if (!this.record?.type) {
            return undefined;
        }
        
        // If type is already a number, use it directly
        if (typeof this.record.type === 'number') {
            return this.record.type;
        }
        
        // If type is an _id (string/ObjectId), check for stored original type
        if ((this.record as any).__originalType !== undefined) {
            return (this.record as any).__originalType;
        }
        
        // Fallback: return undefined if we can't determine the numeric type
        return undefined;
    }

    ngOnInit() {
        const numericType = this.getNumericType();
        if (numericType !== undefined) {
            this.buildTrackKeys(numericType);
            this.previousType = numericType;
        }
    }

    /**
     * Keeps stageMilestone consistent with the shape of the current track.
     *
     * These are *repairs* of derived state, not user edits, so they must never
     * call emitValue(). emitValue() marks every key it declares as touched, and
     * dynamic-form-v2 submits touched keys - so a repair pass here used to drag
     * the whole reward array into the payload of a form the user never edited,
     * writing back whatever happened to be in memory.
     *
     * They still emit, via emitStageMilestoneRepair(), so the corrected value
     * is persisted exactly as it was before - just without the reward arrays
     * riding along.
     */
    async ngDoCheck() {
        const numericType = this.getNumericType();
        if (numericType !== undefined && numericType !== this.previousType) {
            this.previousType = numericType;
            await this.buildTrackKeys(numericType);
        }

        // Handle stageMilestone logic for EO tracks (always check, not just on type change)
        if (numericType === 6) {
            // Always ensure EO tracks have empty stageMilestone (clear any existing milestones)
            if (!this.record.stageMilestone || this.record.stageMilestone.length > 0) {
                this.record.stageMilestone = [];
                this.emitStageMilestoneRepair();
            }
        } else if (numericType !== undefined && this.fixedStageMilestoneTypes.includes(numericType)) {
            // stageMilestone is backend-enforced for these types; nothing to reconcile here.
        } else if (numericType !== undefined && this.previousType === numericType) {
            // Handle non-EO tracks only when type hasn't changed (to avoid infinite loops)
            // Check if current track is single-stage type
            const isSingleStage = this.trackKeys.length > 0 &&
                this.trackKeyType[this.trackKeys[0]] === 'singleStage' &&
                !this.multiKeyStages;
            
            if (isSingleStage) {
                // For single-stage tracks, stageMilestone should be a single-element array
                // It should not expand to match the number of rewards in the stage
                if (!this.record.stageMilestone) {
                    this.record.stageMilestone = [0];
                    this.emitStageMilestoneRepair();
                } else if (this.record.stageMilestone.length !== 1) {
                    // Preserve the first value if it exists, otherwise use 0
                    const firstValue = this.record.stageMilestone.length > 0 ? this.record.stageMilestone[0] : 0;
                    this.record.stageMilestone = [firstValue];
                    this.emitStageMilestoneRepair();
                }
            } else {
                // For multi-stage tracks, ensure stageMilestone matches the number of stages
                if (!this.record.stageMilestone) {
                    this.record.stageMilestone = [];
                }

                const targetLength = this.multiKeyStages ? 
                    this.trackKeys.length : 
                    (this.record[this.trackKeys[0]]?.length || 0);

                if (this.record.stageMilestone.length !== targetLength) {
                    this.record.stageMilestone = this.record.stageMilestone
                        .slice(0, targetLength)
                        .concat(Array(Math.max(0, targetLength - this.record.stageMilestone.length))
                        .fill(0));
                    this.emitStageMilestoneRepair();
                }
            }
        }
    }

    private buildTrackKeys(type: number) {
        switch (type) {
            case 1: // Daily Login Rewards - 7 days
                this.trackKeys = [
                    'dailyRewardDay1', 'dailyRewardDay2', 'dailyRewardDay3',
                    'dailyRewardDay4', 'dailyRewardDay5', 'dailyRewardDay6', 'dailyRewardDay7'
                ];
                this.multiKeyStages = true;
                this.trackKeyLabels = ['Day 1 Rewards', 'Day 2 Rewards', 'Day 3 Rewards', 'Day 4 Rewards', 'Day 5 Rewards', 'Day 6 Rewards', 'Day 7 Rewards'];
                break;
            case 2: // Daily Login Rewards - Day 30
                this.trackKeys = ['dailyRewardDay30'];
                this.trackKeyLabels = ['Day 30 Rewards'];
                this.multiKeyStages = false;
                break;
            case 3: // Season Pass - Free track
                this.trackKeys = ['free'];
                this.trackKeyLabels = ['Free Stage'];
                this.milestoneLabel = 'Target';
                this.multiKeyStages = false;
                if (!this.record.free) {
                    this.record.free = [];
                }
                this.normalizeTrackStages('free');
                break;
            case 4: // Season Pass - Paid track
                this.trackKeys = ['paid'];
                this.trackKeyLabels = ['Paid Stage'];
                this.milestoneLabel = 'Target'; 
                this.multiKeyStages = false;
                if (!this.record.paid) {
                    this.record.paid = [];
                }
                this.normalizeTrackStages('paid');
                break;
            case 5: // Friends Invited
                this.trackKeys = ['friends'];
                this.trackKeyLabels = ['Friends Invited'];
                this.milestoneLabel = 'Friends Invited';
                this.multiKeyStages = false;
                if (!this.record.friends) {
                    this.record.friends = [];
                }
                this.normalizeTrackStages('friends');
                break;
            case 6: // Endless Offer
                this.trackKeys = ['EOrewards'];
                this.trackKeyLabels = ['Endless Offer Rewards'];
                this.touchIsLoop = true;
                this.multiKeyStages = false;
                
                // Immediately clear any existing milestones when switching to EO track
                if (this.record.stageMilestone && this.record.stageMilestone.length > 0) {
                    this.record.stageMilestone = [];
                }
                break;
            case 7: // Co-Op
                this.trackKeys = ['coop'];
                this.trackKeyLabels = ['Co-Op Stage'];
                this.milestoneLabel = 'Reward Target';
                this.multiKeyStages = false;
                this.touchIsLoop = true;
                this.showIsLoop = true;
                if (!this.record.coop) {
                    this.record.coop = [];
                }
                this.normalizeTrackStages('coop');
                break;
            case 8: // Mini Games
                this.trackKeys = ['miniGamesRewards'];
                this.trackKeyLabels = ['Mini Games Rewards'];
                this.multiKeyStages = false;
                if (!this.record.miniGamesRewards) {
                    this.record.miniGamesRewards = [];
                }
                this.normalizeTrackStages('miniGamesRewards');
                this.touchIsLoop = true;
                this.showIsLoop = true;
                break;
            case 9: // Seasonal Prizes
                this.trackKeys = ['seasonalPrizes'];
                this.trackKeyLabels = ['Seasonal Prizes'];
                this.multiKeyStages = false;
                if (!this.record.seasonalPrizes) {
                    this.record.seasonalPrizes = [];
                }
                this.normalizeTrackStages('seasonalPrizes');
                break;
            case 10: // Daily Activities
                this.trackKeys = ['dailyActivitiesRewards'];
                this.trackKeyLabels = ['Daily Activities'];
                this.multiKeyStages = false;
                if (!this.record.dailyActivitiesRewards) {
                    this.record.dailyActivitiesRewards = [];
                }
                this.normalizeTrackStages('dailyActivitiesRewards');
                break;
            case 11: // Perk Daily Reward
                this.trackKeys = ['perkDailyReward'];
                this.trackKeyLabels = ['Perk Daily Reward'];
                this.multiKeyStages = false;
                if (!this.record.perkDailyReward) {
                    this.record.perkDailyReward = [];
                }
                this.normalizeTrackStages('perkDailyReward');
                break;
            case 12: // Gifting Tree
                this.trackKeys = ['giftingTreeV2Rewards'];
                this.trackKeyLabels = ['Gifting Tree Day'];
                this.milestoneLabel = 'Day Target';
                this.multiKeyStages = false;
                if (!this.record.giftingTreeV2Rewards) {
                    this.record.giftingTreeV2Rewards = [];
                }
                this.normalizeTrackStages('giftingTreeV2Rewards');
                break;
            default:
                this.trackKeys = ['error'];
                this.trackKeyLabels = ['Error: Could not match track type'];
        }
    }

    onChange(key: string, event: any, stageIndex: number | null = null) {

        if (key === 'stageMilestone' && stageIndex !== null) {
            this.record.stageMilestone[stageIndex] = event.value;
        } else if(key === 'EOrewards') {
            this.record.EOrewards = event.record.EOrewards;
            this.record.EObundles = event.record.EObundles;
        } else if (stageIndex !== undefined && stageIndex !== null) {
            this.record[key][stageIndex] = event;
        } else {
            this.record[key] = event;
        }

        this.emitValue(key);
    }

    addStage(key: string) {
        // Add an empty stage array - users will add rewards via the prizes-card-form-group interface
        // This follows the same pattern as season-pass-tracks-field component
        this.record[key] = [...this.record[key], []];

        if (this.record.stageMilestone) {
            this.record.stageMilestone[this.record[key].length - 1] = 0;
        } else {
            this.record.stageMilestone = [];
        }

        this.onChange(key, this.record[key]);
        
        setTimeout(() => {
            const orderList = document.getElementById('orderList-' + key);
            if (orderList) {
                const listContent = orderList.querySelector('.p-orderlist-list');
                if (listContent) {
                    listContent.scrollTo({
                        top: listContent.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }
        }, 100);
    }

    removeStage(key: string, index: number): void {
        this.record[key].splice(index, 1);
        this.record.stageMilestone.splice(index, 1);
        this.onChange(key, this.record[key]);
    }

    assignStages(stages: any[]) {
        // This method is called when stages are reordered
        // The stages parameter already contains the reordered array
        // We just need to emit the change to save it
        this.emitValue(this.trackKeys[0]); // Use the first track key for the emit
    }

    /**
     * Normalizes track stages to ensure each stage is a valid array.
     * This fixes issues where stages might be null, undefined, or invalid objects.
     * @param trackKey The key of the track to normalize (e.g., 'free', 'paid', etc.)
     */
    private normalizeTrackStages(trackKey: string): void {
        if (this.record[trackKey] === null || this.record[trackKey] === undefined) {
            this.record[trackKey] = [];
            return;
        }

        if (!Array.isArray(this.record[trackKey])) {
            console.warn(
                `[rewards-track-input] ${trackKey} is not an array; resetting it.`,
                this.record[trackKey]
            );
            this.record[trackKey] = [];
            return;
        }

        // Ensure each stage within the track is a valid array.
        //
        // This runs on load, and the record it mutates is the one the form will
        // submit - so a stage replaced here is a stage blanked in the database.
        // Only genuinely empty stages may be coerced to []; a stage that holds a
        // line item is wrapped, never discarded.
        for (let i = 0; i < this.record[trackKey].length; i++) {
            const stage = this.record[trackKey][i];

            if (Array.isArray(stage)) {
                continue;
            }

            if (stage === null || stage === undefined) {
                this.record[trackKey][i] = [];
                continue;
            }

            if (typeof stage === 'object' && Object.keys(stage).length > 0) {
                // A single line item stored where a stage array was expected.
                // Wrapping keeps the reward; replacing it with [] would lose it.
                console.warn(
                    `[rewards-track-input] ${trackKey}[${i}] was a bare object; wrapping it as a stage.`,
                    stage
                );
                this.record[trackKey][i] = [stage];
                continue;
            }

            console.warn(
                `[rewards-track-input] ${trackKey}[${i}] has an unusable shape; resetting it.`,
                stage
            );
            this.record[trackKey][i] = [];
        }
    }

    /**
     * Every key this component owns for the current track type.
     *
     * stageMilestone is always in the list because adding, removing or
     * reordering a stage rewrites it alongside the track array.
     */
    private declaredKeys(key?: string): string[] {
        const keys = [...this.trackKeys, 'stageMilestone'];

        if (this.touchIsLoop) {
            keys.push('isLoop');
        }

        if (key === 'EOrewards') {
            keys.push('EObundles');
        }

        // isTemplate is always tracked: the API skips line-item validation for
        // templates, and it can only do that if the flag reaches it.
        keys.push('isTemplate');

        return keys;
    }

    /**
     * Keys this component has actually changed, accumulated across emits.
     *
     * field.customFields is rebuilt on every emit, so touched flags cannot be
     * derived from the current emit alone - a stageMilestone repair firing
     * after a reward edit would drop the reward edit from the payload.
     */
    private readonly touchedKeys = new Set<string>(['isTemplate']);

    private syncCustomFields(key?: string) {
        // Union with touchedKeys so a key touched earlier can never fall out of
        // the list and lose its flag.
        const keys = new Set<string>([...this.declaredKeys(key), ...this.touchedKeys]);
        this.field.customFields = [...keys].map(k => ({
            key: k,
            touched: this.touchedKeys.has(k)
        }));
    }

    private emitRecord() {
        this.onRecordChange.emit({
            event: { record: this.record, updateAllCustomFields: true },
            field: this.field,
            updateFullRecord: true
        });
    }

    /**
     * A user edit. Anything this track owns may have changed with it - adding a
     * stage, for instance, rewrites both the track array and stageMilestone.
     */
    emitValue(key: string) {
        this.declaredKeys(key).forEach(k => this.touchedKeys.add(k));
        this.syncCustomFields(key);
        this.emitRecord();
    }

    /**
     * A repair of derived state, not a user edit.
     *
     * Persists the corrected stageMilestone while leaving the reward arrays out
     * of the payload - writing those back from a form nobody had edited is what
     * silently emptied three stages of a mini-games track.
     */
    private emitStageMilestoneRepair() {
        this.touchedKeys.add('stageMilestone');
        this.syncCustomFields();
        this.emitRecord();
    }
}
